import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Tasks.css";
import FormGuidelines from "../components/FormGuidelines";
import FieldToast from "../components/FieldToast";
import Pagination from "../components/Pagination";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { LoadingState, EmptyState } from "../components/LoadingState";
import {
  createTaskApi,
  deleteTaskApi,
  deleteTaskFileApi,
  getTasks,
  updateTaskApi,
  updateTaskStatusApi,
  uploadTaskFileApi,
  addCommentApi,
  deleteCommentApi,
} from "../api/tasksApi";
import {
  ADMIN_ROLES,
  hasAnyRole,
} from "../utils/roles";
import { normalizeDepartment, POSITION_OPTIONS } from "../utils/departments";

// ── Column definitions (frontend internal values) ────────────
const columns = ["PENDING", "IN_PROGRESS", "TIME_REQUEST", "DONE"];
const columnTitles = {
  PENDING:      "To Do",
  IN_PROGRESS:  "In Progress",
  TIME_REQUEST: "Delay Request",
  DONE:         "Done",
};
const columnIcons = {
  PENDING:      "📋",
  IN_PROGRESS:  "⚙️",
  TIME_REQUEST: "⏳",
  DONE:         "✅",
};

// ── API value maps ────────────────────────────────────────────
const STATUS_TO_API = {
  PENDING:      "todo",
  IN_PROGRESS:  "in progress",
  TIME_REQUEST: "suspended",
  DONE:         "done",
};

const STATUS_FROM_API = {
  "todo":         "PENDING",
  "in progress":  "IN_PROGRESS",
  "suspended":    "TIME_REQUEST",
  "done":         "DONE",
};

const PRIORITY_TO_API = {
  LOW:    "low",
  MEDIUM: "medium",
  HIGH:   "high",
};

const PRIORITY_FROM_API = {
  low:    "LOW",
  medium: "MEDIUM",
  high:   "HIGH",
};

// Max size per file (MB). Make it match the limit on your backend (multer limits.fileSize / nginx client_max_body_size)
const MAX_FILE_SIZE_MB = 20;

const normalizeId = (id) => {
  if (!id) return "";
  if (typeof id === "object") {
    if (id.$oid) return String(id.$oid).trim();
    if (id._id)  return normalizeId(id._id);
    if (id.id)   return normalizeId(id.id);
  }
  return String(id).trim();
};

const normalizeTaskFromApi = (task) => {
  const statusKey = task.status?.toLowerCase?.()?.trim?.() || "";

  const assignedArr = Array.isArray(task.assignedTo) ? task.assignedTo : [];
  const rawAssignee = assignedArr[0];
  const assigneeUserId = normalizeId(rawAssignee?._id || rawAssignee?.id || rawAssignee);

  // ✅ كل الـ assignees (مش بس الأول)
  const assigneeUserIds = assignedArr
    .map((a) => normalizeId(a?._id || a?.id || a))
    .filter(Boolean);

  return {
    ...task,
    assigneeUserId,
    assigneeUserIds,
    assigneeUserName:
      rawAssignee?.userName ||
      rawAssignee?.name     ||
      rawAssignee?.email    ||
      "",
    status:   STATUS_FROM_API[statusKey] || "PENDING",
    priority: PRIORITY_FROM_API[task.priority?.toLowerCase()] || "MEDIUM",
    deadline: task.dueDate,
    comments:    task.comments    || [],
    attachments:
      task.attachments ||
      task.files ||
      task.images ||
      [],
  };
};

// ── Misc constants ────────────────────────────────────────────
const PRIORITIES = ["LOW", "MEDIUM", "HIGH"];
const priorityColors = {
  LOW:    "#22c55e",
  MEDIUM: "#f59e0b",
  HIGH:   "#ef4444",
};

// ── Helpers ───────────────────────────────────────────────────
const formatTime = (ts) => {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return ts;
  }
};

const formatDeadline = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleString("en-US", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch {
    return dateStr;
  }
};

const isOverdue = (deadline, status) =>
  !!(deadline && status !== "DONE" && new Date(deadline) < new Date());

const FILE_ICONS = {
  "application/msword": "📄",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "📄",
  "application/vnd.ms-excel": "📊",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "📊",
  "application/zip": "🗜️",
  "text/plain": "📝",
};
const getFileIcon = (mime) => FILE_ICONS[mime] || "📎";

const getUserId         = (user) => normalizeId(user?._id || user?.id || "");
const getUserName       = (user) => user?.userName || user?.name || user?.email || "User";
const getUserDepartment = (user) =>
  normalizeDepartment(user?.department || user?.Department || "");

function canManageTaskAsCreator(task, currentUserId, isAdmin) {
  if (!task || !currentUserId) return false;
  if (normalizeId(task.createdByUserId) === normalizeId(currentUserId)) return true;
  if (!normalizeId(task.createdByUserId) && isAdmin) return true;
  return false;
}

// ✅ بيشيك على كل الـ assignees مش الأول بس
const isAssignee = (task, currentUserId) => {
  if (!task || !currentUserId) return false;
  const me = normalizeId(currentUserId);
  if (Array.isArray(task.assigneeUserIds) && task.assigneeUserIds.length > 0) {
    return task.assigneeUserIds.includes(me);
  }
  return Boolean(task.assigneeUserId && task.assigneeUserId === me);
};

function localDatetimeToISO(datetimeLocalStr) {
  if (!datetimeLocalStr) return "";
  const date = new Date(datetimeLocalStr);
  if (isNaN(date.getTime())) return datetimeLocalStr;

  const offsetMinutes = -date.getTimezoneOffset(); // Cairo = 180
  const sign   = offsetMinutes >= 0 ? "+" : "-";
  const absMin = Math.abs(offsetMinutes);
  const hh     = String(Math.floor(absMin / 60)).padStart(2, "0");
  const mm     = String(absMin % 60).padStart(2, "0");
  const pad    = (n) => String(n).padStart(2, "0");

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:00${sign}${hh}:${mm}`
  );
}

// ── Download helper (bypasses CORS) ──────────────────────────
const downloadFile = async (url, name) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, "_blank");
  }
};

// ─────────────────────────────────────────────────────────────
export default function Tasks() {
  const navigate = useNavigate();
  const { users, currentUser } = useAuth();
  const currentUserId = getUserId(currentUser);
  const isAdmin       = hasAnyRole(currentUser, ADMIN_ROLES);
  const canReviewTasks = isAdmin;

  const [tasks,        setTasks]        = useState([]);
  const [summaryRows,  setSummaryRows]  = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [showModal,    setShowModal]    = useState(false);
  const [editingTask,  setEditingTask]  = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [viewMode,     setViewMode]     = useState("board");
  const [expandedCard, setExpandedCard] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewPdf,   setPreviewPdf]   = useState(null);
  const [dragOverCard, setDragOverCard] = useState(null);
  const [taskDetailModal, setTaskDetailModal] = useState(null);

  // { [taskId]: { done, total } } — حالة الرفع الجارية لكل تاسك
  const [uploading, setUploading] = useState({});

  const [delayModal,  setDelayModal]  = useState(null);
  const [delayReason, setDelayReason] = useState("");

  const [search,         setSearch]         = useState("");
  const [filterUser,     setFilterUser]     = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [showMyTasks,    setShowMyTasks]    = useState(false);
  const [currentPage,   setCurrentPage]   = useState(1);
  const itemsPerPage = 10;

  // ✅ نسخة دايمًا محدثة من الـ tasks عشان نتفادى الـ stale closure داخل الدوال async
  const tasksRef = useRef([]);
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  // Field refs for validation toasts
  const titleRef = useRef(null);
  const assigneeRef = useRef(null);
  const deptRef = useRef(null);
  const priorityRef = useRef(null);

  // Field toast state
  const [fieldToast, setFieldToast] = useState({
    show: false,
    message: "",
    targetRef: null,
  });

  const showFieldToast = (message, targetRef) => {
    setFieldToast({
      show: true,
      message,
      targetRef,
    });
    setTimeout(() => {
      setFieldToast((prev) => ({ ...prev, show: false }));
    }, 3000);
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      const searchLower = search.toLowerCase().trim();
      const matchesSearch = !searchLower || (
        t.title?.toLowerCase().includes(searchLower) ||
        t.assigneeUserName?.toLowerCase().includes(searchLower)
      );
      const matchesUser     = !filterUser     || (t.assigneeUserIds || []).includes(normalizeId(filterUser));
      const matchesPriority = !filterPriority || t.priority === filterPriority;
      return matchesSearch && matchesUser && matchesPriority;
    });
  }, [tasks, search, filterUser, filterPriority]);

  // Pagination logic
  const paginatedTasks = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredTasks.slice(startIndex, endIndex);
  }, [filteredTasks, currentPage]);

  const totalPages = Math.ceil(filteredTasks.length / itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterUser, filterPriority]);

  const filteredSummaryRows = useMemo(() => {
    if (!search && !filterUser) return summaryRows;

    const searchLower = search.toLowerCase().trim();
    return summaryRows.filter((row) => {
      const matchesSearch = !searchLower || (
        row.assigneeUserName?.toLowerCase().includes(searchLower)
      );
      const matchesUser = !filterUser || row.assigneeUserId === normalizeId(filterUser);
      return matchesSearch && matchesUser;
    });
  }, [summaryRows, search, filterUser]);

  // Always show all priority options
  const availablePriorities = PRIORITIES;

  const [activityLog, setActivityLog] = useState({});

  const emptyTaskData = {
    title: "", description: "", assignedTo: [],
    deadline: "", status: "PENDING", priority: "MEDIUM",
    departmentPick: "", hasDeadline: false,
  };
  const [taskData, setTaskData] = useState(emptyTaskData);

  // ── Summary ───────────────────────────────────────────────
  const computeSummary = useCallback((arr) => {
    const summaryMap = {};
    arr.forEach((task) => {
      const uid = task.assigneeUserId;
      if (!uid) return;
      if (!summaryMap[uid]) {
        // Find user from users array to get role
        const user = (users || []).find(u => normalizeId(u._id || u.id) === uid);
        summaryMap[uid] = {
          assigneeUserId:     uid,
          assigneeUserName:   task.assigneeUserName,
          assigneeDepartment: task.assigneeDepartment || "—",
          assigneeRole:       user?.role || "—",
          total: 0, done: 0, overdue: 0,
          inProgress: 0, pending: 0,
          timeRequest: 0,
          lastUpdated: null,
        };
      }
      const row = summaryMap[uid];
      row.total++;
      if (task.status === "DONE")          row.done++;
      if (task.status === "IN_PROGRESS")   row.inProgress++;
      if (task.status === "PENDING")       row.pending++;
      if (task.status === "TIME_REQUEST")  row.timeRequest++;
      if (isOverdue(task.deadline, task.status)) row.overdue++;
      const upd = task.updatedAt || task.createdAt;
      if (upd && (!row.lastUpdated || new Date(upd) > new Date(row.lastUpdated)))
        row.lastUpdated = upd;
    });
    setSummaryRows(Object.values(summaryMap));
  }, [users]);

  // ✅ الـ summary بيتحسب أوتوماتيك كل ما الـ tasks تتغير (مفيش حاجة تتنسي)
  useEffect(() => { computeSummary(tasks); }, [tasks, computeSummary]);

  // ── Load tasks ────────────────────────────────────────────
  // silent = true  → تحديث في الخلفية من غير شاشة Loading (بيحافظ على الكارت المفتوح)
  const loadTasks = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingTasks(true);
    try {
      const assigneeScope = isAdmin ? filterUser : currentUserId;
      const filters = {
        ...(assigneeScope ? { assigneeUserId: assigneeScope } : {}),
        priority:   filterPriority ? PRIORITY_TO_API[filterPriority] : "",
      };

      const tasksData = await getTasks(filters);

      let rawTasks = [];
      if      (Array.isArray(tasksData?.data))          rawTasks = tasksData.data;
      else if (Array.isArray(tasksData?.data?.tasks))   rawTasks = tasksData.data.tasks;
      else if (Array.isArray(tasksData?.tasks))         rawTasks = tasksData.tasks;
      else if (Array.isArray(tasksData?.data?.data))    rawTasks = tasksData.data.data;

      setTasks(rawTasks.map(normalizeTaskFromApi));
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to load tasks");
    } finally {
      if (!silent) setLoadingTasks(false);
    }
  }, [filterUser, filterPriority, isAdmin, currentUserId]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  // ── Activity log ──────────────────────────────────────────
  const logActivity = useCallback((taskId, text) => {
    setActivityLog((prev) => ({
      ...prev,
      [taskId]: [...(prev[taskId] || []), { text, time: Date.now() }],
    }));
  }, []);

  // ── Modal open/close ──────────────────────────────────────
  const openCreateModal = () => {
    setEditingTask(null);
    setTaskData(emptyTaskData);
    setSelectedUser(null);
    setShowModal(true);
  };

  const openEditModal = (task) => {
    const matchedUser =
      (users || []).find((u) => getUserId(u) === task.assigneeUserId) ||
      {
        _id: task.assigneeUserId,
        userName: task.assigneeUserName,
        department: normalizeDepartment(task.assigneeDepartment),
      };

    // Format deadline for datetime-local input (use local time to avoid timezone issues)
    let formattedDeadline = "";
    if (task.deadline) {
      try {
        const date = new Date(task.deadline);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        formattedDeadline = `${year}-${month}-${day}T${hours}:${minutes}`;
      } catch (e) {
        formattedDeadline = task.deadline;
      }
    }

    setEditingTask(task);
    setTaskData({
      title:        task.title,
      description:  task.description,
      assignedTo:   task.assigneeUserIds?.length ? task.assigneeUserIds : (task.assigneeUserId ? [task.assigneeUserId] : []),
      deadline:     formattedDeadline,
      status:       task.status,
      priority:     task.priority,
      departmentPick: normalizeDepartment(task.assigneeDepartment) || "",
      hasDeadline:  !!task.deadline,
    });
    setSelectedUser(matchedUser);
    setShowModal(true);
  };

  // ── Save task ─────────────────────────────────────────────
  const handleSaveTask = () => {
    const deptFromUser       = getUserDepartment(selectedUser);
    const assigneeDepartment = normalizeDepartment(deptFromUser || taskData.departmentPick || "");

    // Title validation
    if (!taskData.title || (typeof taskData.title === 'string' && taskData.title.trim() === "")) {
      showFieldToast("Task title is required", titleRef);
      return;
    }

    if (typeof taskData.title === 'string' && taskData.title.trim().length < 3) {
      showFieldToast("Task title must be at least 3 characters", titleRef);
      return;
    }

    if (typeof taskData.title === 'string' && taskData.title.trim().length > 100) {
      showFieldToast("Task title must be less than 100 characters", titleRef);
      return;
    }

    // Description validation
    if (taskData.description && typeof taskData.description === 'string' && taskData.description.trim().length > 500) {
      showFieldToast("Description must be less than 500 characters", null);
      return;
    }

    // Assignee validation
    if (!taskData.assignedTo || taskData.assignedTo.length === 0) {
      showFieldToast("Please assign at least one user to this task", assigneeRef);
      return;
    }

    // Department validation
    if (!assigneeDepartment) {
      showFieldToast("Department is required", deptRef);
      return;
    }

    // Priority validation
    if (!taskData.priority) {
      showFieldToast("Please select a priority level", priorityRef);
      return;
    }

    // Status validation
    if (!taskData.status) {
      showFieldToast("Please select a status", null);
      return;
    }

    // Deadline validation - auto-set to 30 days if not provided (only for new tasks)
    let dueDate = taskData.deadline;

    if (!dueDate || (typeof dueDate === "string" && dueDate.trim() === "")) {
      if (editingTask && editingTask.deadline) {
        // keep existing deadline as-is (already stored with correct offset)
        dueDate = editingTask.deadline;
      } else {
        // auto-set 30 days from now
        const future = new Date();
        future.setDate(future.getDate() + 30);
        const pad = (n) => String(n).padStart(2, "0");
        const str =
          `${future.getFullYear()}-${pad(future.getMonth() + 1)}-${pad(future.getDate())}` +
          `T${pad(future.getHours())}:${pad(future.getMinutes())}`;
        dueDate = localDatetimeToISO(str);
      }
    } else {
      // حول الوقت المحلي لـ ISO مع الـ timezone offset
      dueDate = localDatetimeToISO(dueDate);
    }

    // Validate deadline is in the future if provided (skip validation for editing existing tasks with past deadlines)
    if (dueDate && typeof dueDate === 'string' && dueDate.trim() !== "" && !editingTask) {
      const deadlineDate = new Date(dueDate);
      const now = new Date();
      if (deadlineDate < now) {
        showFieldToast("Deadline must be in the future", null);
        return;
      }
    }

    const payload = {
      title:       typeof taskData.title === 'string' ? taskData.title.trim() : String(taskData.title || ""),
      description: typeof taskData.description === 'string' ? taskData.description.trim() : String(taskData.description || ""),
      assignedTo:  taskData.assignedTo,
      dueDate:     dueDate,
      status:      STATUS_TO_API[taskData.status],
      priority:    PRIORITY_TO_API[taskData.priority],
    };

    const action = editingTask
      ? updateTaskApi(editingTask._id, payload)
      : createTaskApi(payload);

    action
      .then((savedTask) => {
        const normalized = normalizeTaskFromApi(savedTask);
        logActivity(normalized._id, `${editingTask ? "Edited" : "Created"} by ${getUserName(currentUser)}`);
        toast.success(editingTask ? "Task updated" : "Task created");
        loadTasks({ silent: true });
        setShowModal(false);
        setEditingTask(null);
        setSelectedUser(null);
        setTaskData(emptyTaskData);
      })
      .catch((err) => {
        console.error(err);
        toast.error(err?.message || "Failed to save task");
      });
  };

  // ── Drag & drop ───────────────────────────────────────────
  const onDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.droppableId === result.source.droppableId) return;
    const task = tasks.find((t) => normalizeId(t._id) === result.draggableId);
    if (!task) return;
    if (!isAssignee(task, currentUserId)) {
      toast.error("Only the assignee can move this card between columns");
      return;
    }
    updateStatus(result.draggableId, result.destination.droppableId);
  };

  // ── Update status ─────────────────────────────────────────
  // ✅ بيستخدم functional setState + tasksRef، وبيرجّع Promise عشان نقدر نستناه.
  // ✅ لو فشل بيرجّع الكارت ده بس لحالته القديمة (مش بيمسح باقي الداتا زي ما كان بيحصل)
  const updateStatus = (id, status, note = "") => {
    const normalizedId = normalizeId(id);
    const prevTask = tasksRef.current.find((t) => normalizeId(t._id) === normalizedId);
    if (!prevTask) return Promise.resolve();

    const prevStatus = prevTask.status;
    const prevNote   = prevTask.statusNote;

    setTasks((prev) =>
      prev.map((t) =>
        normalizeId(t._id) === normalizedId ? { ...t, status, statusNote: note } : t
      )
    );

    logActivity(id, `${getUserName(currentUser)} → ${columnTitles[status] || status}`);
    toast.success(`Status → ${columnTitles[status]}`);

    return updateTaskStatusApi(normalizedId, {
      status:     STATUS_TO_API[status],
      statusNote: note,
    }).catch((err) => {
      console.error(err);
      toast.error(err?.message || "Failed to update status");
      setTasks((prev) =>
        prev.map((t) =>
          normalizeId(t._id) === normalizedId ? { ...t, status: prevStatus, statusNote: prevNote } : t
        )
      );
    });
  };

  // ── Delete task ───────────────────────────────────────────
  const deleteTask = (id) => {
    const task = tasks.find((t) => normalizeId(t._id) === normalizeId(id));
    if (!canManageTaskAsCreator(task, currentUserId, isAdmin)) {
      toast.error("Only the person who created this task can delete it");
      return;
    }
    deleteTaskApi(id)
      .then(() => {
        setTasks((prev) => prev.filter((t) => normalizeId(t._id) !== normalizeId(id)));
        toast.success("Task deleted");
      })
      .catch((err) => {
        console.error(err);
        toast.error(err?.message || "Failed to delete task");
      });
  };

  // ── Delay ─────────────────────────────────────────────────
  const submitDelay = () => {
    if (!delayReason || (typeof delayReason === 'string' && !delayReason.trim())) { toast.error("Provide a reason"); return; }
    updateStatus(delayModal, "TIME_REQUEST", delayReason);
    setDelayModal(null);
    setDelayReason("");
  };

  // ── Comments ──────────────────────────────────────────────
  const addComment = async (taskId, text) => {
    if (!text || (typeof text === 'string' && !text.trim())) return;
    try {
      const newComment = await addCommentApi(taskId, { comment: text });
      toast.success("Comment added");
      logActivity(taskId, `${getUserName(currentUser)} commented`);

      // Optimistic update - add comment to local state
      setTasks((prevTasks) =>
        prevTasks.map((task) => {
          if (normalizeId(task._id) === normalizeId(taskId)) {
            return {
              ...task,
              comments: [
                ...(task.comments || []),
                {
                  _id: newComment?._id || newComment?.id || Date.now(),
                  text: text,
                  comment: text,
                  author: {
                    userName: getUserName(currentUser),
                    name: getUserName(currentUser),
                  },
                  createdAt: new Date().toISOString(),
                },
              ],
            };
          }
          return task;
        })
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to add comment");
    }
  };

  const deleteComment = async (taskId, commentId, commentAuthor) => {
    // Check if user is the comment author or admin
    const isCommentAuthor = commentAuthor === getUserName(currentUser);

    if (!isCommentAuthor && !isAdmin) {
      toast.error("You can only delete your own comments");
      return;
    }

    try {
      await deleteCommentApi(taskId, commentId);
      toast.success("Comment deleted");
      logActivity(taskId, `${getUserName(currentUser)} deleted a comment`);

      // Optimistic update - remove comment from local state
      setTasks((prevTasks) =>
        prevTasks.map((task) => {
          if (normalizeId(task._id) === normalizeId(taskId)) {
            return {
              ...task,
              comments: (task.comments || []).filter(
                (c) => (c._id || c.id || c.commentId) !== commentId
              ),
            };
          }
          return task;
        })
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to delete comment");
    }
  };

  // ── Files ─────────────────────────────────────────────────
  const handleFileDrop = (e, taskId) => {
    // لو اللي اتسحب مش ملفات (مثلاً كارت بيتحرك) متعملش حاجة
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverCard(null);
    if (e.dataTransfer.files.length > 0) {
      processFiles(taskId, e.dataTransfer.files);
    }
  };

  /**
   * ✅ رفع عدة ملفات:
   *  1) بنحوّل الـ FileList لـ Array فورًا (الـ FileList بيتفرّغ لو الـ input اتعمله reset)
   *  2) بنرفع الملفات واحد ورا التاني (بيمنع race conditions على نفس الـ task في الـ backend
   *     وبيخلي كل ملف يفشل لوحده من غير ما يوقّع الباقي)
   *  3) بنقفل الخطأ الصامت: لو الـ API رجّع success:false من غير ما يرمي error بنعتبره فشل
   *  4) في الآخر بنعمل refresh في الخلفية (silent) فالملفات تظهر فورًا من غير reload
   */
  const processFiles = async (taskId, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    if (uploading[taskId]) {
      toast.error("An upload is already in progress for this task");
      return;
    }

    const maxBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
    const validFiles = [];
    files.forEach((f) => {
      if (f.size > maxBytes) {
        toast.error(`"${f.name}" is larger than ${MAX_FILE_SIZE_MB}MB`);
      } else if (f.size === 0) {
        toast.error(`"${f.name}" is empty`);
      } else {
        validFiles.push(f);
      }
    });
    if (validFiles.length === 0) return;

    const task = tasksRef.current.find((t) => normalizeId(t._id) === normalizeId(taskId));

    setUploading((prev) => ({ ...prev, [taskId]: { done: 0, total: validFiles.length } }));

    let okCount = 0;
    let failCount = 0;

  for (const file of validFiles) {
  try {
    const res = await uploadTaskFileApi(taskId, [file]); // ← ملف واحد
    if (res && (res.success === false || res.status === "error" || res.error)) {
      throw new Error(res.message || res.error || "Upload failed");
    }
    okCount++;
  } catch (err) {
    failCount++;
    console.error("Upload failed:", err?.response?.status, err?.data, err);
    toast.error(`"${file.name}": ${err?.message || "Upload failed"}`);
  }
  setUploading((prev) => ({
    ...prev,
    [taskId]: { done: okCount + failCount, total: validFiles.length },
  }));
}

    if (okCount > 0) {
      toast.success(okCount === 1 ? "File uploaded" : `${okCount} files uploaded`);
      logActivity(taskId, `${getUserName(currentUser)} uploaded ${okCount} file(s)`);

      // لو اللي رفع هو الـ assignee والتاسك لسه To Do → يتحول In Progress
      if (task && task.status === "PENDING" && isAssignee(task, currentUserId)) {
        await updateStatus(taskId, "IN_PROGRESS");
      }

      // تحديث في الخلفية: الملفات تظهر فورًا من غير reload ومن غير ما الكارت يقفل
      await loadTasks({ silent: true });
    }

    setUploading((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
  };

  // ── Delete an attachment ──────────────────────────────────
  const handleDeleteAttachment = async (taskId, file) => {
    const fileId = normalizeId(file?._id || file?.id || file?.fileId);
    if (!fileId) {
      console.log("attachment without id:", file);
      toast.error("This file has no id");
      return;
    }
    try {
      await deleteTaskFileApi(taskId, fileId);
      toast.success("Attachment deleted");
      loadTasks({ silent: true });
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    }
  };

  // ── Loading state ─────────────────────────────────────────
  if (loadingTasks) {
    return <LoadingState message="Loading tasks..." />;
  }

  // ─────────────────────────────────────────────────────────
  return (
    <div className="tasks-page">

      {/* HEADER */}
      <div className="tasks-header">
        <div className="header-left">
          <h2>Tasks Board</h2>
          <span className="total-badge">{tasks.length} tasks</span>
        </div>
        <div className="header-right">
          {isAdmin && (
            <>
              {(currentUser?.role === "FOUNDER" || currentUser?.role === "TEAM_LEADER") && (
                <button className="deleted-btn" onClick={() => navigate("/tasks/deleted")}>🗑️ Deleted Tasks</button>
              )}
              <button className="create-btn" onClick={openCreateModal}>+ Create Task</button>
            </>
          )}
        </div>
      </div>

      {/* VIEW TOGGLE */}
      {isAdmin && (
        <div className="tasks-view-toggle-wrap">
          <div className="tasks-view-toggle">
            <button type="button" className={viewMode === "board" ? "active" : ""} onClick={() => setViewMode("board")}>Board</button>
            <button type="button" className={viewMode === "table" ? "active" : ""} onClick={() => setViewMode("table")}>People Table</button>
          </div>
          <p className="people-table-explainer">
            People Table shows a <strong>per-person task summary</strong>. Click a row to filter the board.
          </p>
        </div>
      )}

      {/* FILTERS */}
      <div className="filters-bar">
        <input
          className="search-input"
          placeholder="Search by task title or assignee name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {isAdmin ? (
          <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)}>
            <option value="">All Members</option>
            {(users || []).map((u) => (
              <option key={getUserId(u)} value={getUserId(u)}>{getUserName(u)}</option>
            ))}
          </select>
        ) : (
          <label className="filters-my-tasks-checkbox">
            <input
              type="checkbox"
              checked={showMyTasks}
              onChange={(e) => {
                setShowMyTasks(e.target.checked);
                setFilterUser(e.target.checked ? currentUserId : "");
              }}
            />
            <span>My tasks</span>
          </label>
        )}
        {viewMode === "board" && (
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
            <option value="">All Priorities</option>
            {availablePriorities.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        )}
      </div>

      {/* PEOPLE TABLE */}
      {viewMode === "table" && isAdmin ? (
        <div className="people-table-wrap">
          <table className="people-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Total</th>
                <th>Done</th>
                <th>Overdue</th>
                <th>In Progress</th>
                <th>Pending</th>
                <th>Delay</th>
                <th>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {filteredSummaryRows.length === 0 ? (
                <tr><td colSpan={9} className="people-table-empty">No task data yet</td></tr>
              ) : (
                filteredSummaryRows.map((row) => (
                  <tr
                    key={row.assigneeUserId}
                    className={normalizeId(filterUser) === row.assigneeUserId ? "active" : ""}
                    onClick={() => {
                      setFilterUser((prev) =>
                        normalizeId(prev) === row.assigneeUserId ? "" : row.assigneeUserId
                      );
                      setViewMode("board");
                    }}
                  >
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">{(row.assigneeUserName || "?")[0].toUpperCase()}</div>
                        {row.assigneeUserName}
                      </div>
                    </td>
                    <td>{row.assigneeRole || "—"}</td>
                    <td><span className="count-badge total">{row.total}</span></td>
                    <td><span className="count-badge done">{row.done}</span></td>
                    <td>{row.overdue > 0 ? <span className="count-badge overdue">{row.overdue}</span> : <span className="count-badge zero">0</span>}</td>
                    <td><span className="count-badge inprogress">{row.inProgress}</span></td>
                    <td><span className="count-badge pending">{row.pending}</span></td>
                    <td><span className="count-badge delay">{row.timeRequest}</span></td>
                    <td className="last-updated">{row.lastUpdated ? formatTime(row.lastUpdated) : "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          {/* BOARD */}
          <DragDropContext onDragEnd={onDragEnd}>
          <div className="board">
            {columns.map((col) => {
                const colTasks = filteredTasks.filter((t) => t.status === col);
              return (
                <Droppable droppableId={col} key={col}>
                  {(provided) => (
                    <div
                      className={`column column-${col}`}
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      <div className="column-header">
                        <span>{columnIcons[col]} {columnTitles[col]}</span>
                        <span className="col-count">{colTasks.length}</span>
                      </div>

                      {colTasks.map((task, index) => {
                        const overdue         = isOverdue(task.deadline, task.status);
                        const isExpanded      = expandedCard === task._id;
                        const isDragOver      = dragOverCard === task._id;
                        const assigneeCanDrag = isAssignee(task, currentUserId);
                        const uploadState     = uploading[task._id];

                        return (
                          <Draggable
                            key={String(task._id)}
                            draggableId={String(normalizeId(task._id))}
                            index={index}
                            isDragDisabled={!assigneeCanDrag}
                          >
                            {(provided) => (
                              <div
                                className={`task-card${overdue ? " overdue" : ""}${isDragOver ? " file-drag-over" : ""}${!assigneeCanDrag ? " task-card-no-drag" : ""}`}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                onDragOver={(e) => {
                                  // نتفاعل بس لو اللي بيتسحب ملفات
                                  if (!e.dataTransfer?.types?.includes("Files")) return;
                                  e.preventDefault();
                                  if (dragOverCard !== task._id) setDragOverCard(task._id);
                                }}
                                onDragLeave={() => setDragOverCard(null)}
                                onDrop={(e) => handleFileDrop(e, task._id)}
                              >
                                <div className="priority-stripe" style={{ background: priorityColors[task.priority] }} />

                                <div className="card-top">
                                  <h3 onClick={() => setTaskDetailModal(task)} style={{ cursor: "pointer" }}>{task.title}</h3>
                                  <div className="card-badges">
                                    <span className="priority-badge" style={{ color: priorityColors[task.priority] }}>
                                      {task.priority}
                                    </span>
                                    {overdue && <span className="overdue-badge">Overdue</span>}
                                  </div>
                                </div>

                                {task.description && <p className="task-desc">{task.description}</p>}

                                <div className="task-assignee-row">
                                  <div className="assignee-avatars">
                                    {task.assignedTo && task.assignedTo.length > 0 ? (
                                      task.assignedTo.slice(0, 3).map((assignee, idx) => (
                                        <span key={idx} className="assignee-avatar-small">
                                          {(assignee?.userName || assignee?.name || "?")[0].toUpperCase()}
                                        </span>
                                      ))
                                    ) : (
                                      <span className="assignee-avatar-small">
                                        {(task.assigneeUserName || "?")[0].toUpperCase()}
                                      </span>
                                    )}
                                    {task.assignedTo && task.assignedTo.length > 3 && (
                                      <span className="assignee-more">+{task.assignedTo.length - 3}</span>
                                    )}
                                  </div>
                                  <span className="assignee-name">
                                    {task.assignedTo && task.assignedTo.length > 0
                                      ? task.assignedTo.map(a => a?.userName || a?.name).join(", ")
                                      : task.assigneeUserName}
                                  </span>
                                  {isAssignee(task, currentUserId) ? (
                                    <span className="assignee-badge you">✋ You</span>
                                  ) : (
                                    <span className="assignee-badge locked">🔒</span>
                                  )}
                                </div>

                                <div className="task-meta">
                                  {task.deadline && (
                                    <span className={`meta-deadline${overdue ? " deadline-red" : ""}`}>
                                      📅 {formatDeadline(task.deadline)}
                                    </span>
                                  )}
                                </div>


                                {isAssignee(task, currentUserId) && (
                                  <div className="task-actions">
                                    {task.status === "PENDING" && (
                                      <button onClick={() => updateStatus(task._id, "IN_PROGRESS")}>▶ Start</button>
                                    )}
                                    {task.status === "IN_PROGRESS" && (
                                      <>
                                        <button onClick={() => updateStatus(task._id, "DONE")}>✓ Submit</button>
                                        <button className="delay-btn" onClick={() => setDelayModal(task._id)}>⏳ Delay</button>
                                      </>
                                    )}
                                  </div>
                                )}

                                {canReviewTasks && task.status === "TIME_REQUEST" && (
                                  <div className="approval-box">
                                    <button className="approve-btn" onClick={() => updateStatus(task._id, "IN_PROGRESS")}>✓ Grant Time</button>
                                    <button className="reject-btn"  onClick={() => updateStatus(task._id, "IN_PROGRESS")}>✕ Deny</button>
                                  </div>
                                )}

                                <button
                                  className="expand-btn"
                                  onClick={() => setExpandedCard(isExpanded ? null : task._id)}
                                >
                                  {isExpanded ? "▲ Less" : "▼ More"}
                                </button>

                                {isExpanded && (
                                  <div className="expanded-section">

                                    {/* ── ATTACHMENTS ── */}
                                    <div className="files-section">
                                      <div
                                        className="files-dropzone"
                                        style={uploadState ? { opacity: 0.6, pointerEvents: "none" } : undefined}
                                        onClick={() => document.getElementById(`file-input-${task._id}`)?.click()}
                                      >
                                        <span>
                                          {uploadState
                                            ? `⏳ Uploading ${uploadState.done}/${uploadState.total}...`
                                            : "📎 Drop files here or click to upload (you can select multiple)"}
                                        </span>
                                        <input
                                          id={`file-input-${task._id}`}
                                          type="file"
                                          multiple
                                          hidden
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => {
                                            // ✅ ننسخ الملفات الأول ثم نعمل reset للـ input
                                            // (من غير الـ reset اختيار نفس الملف تاني مش بيشتغل)
                                            const selected = Array.from(e.target.files || []);
                                            e.target.value = "";
                                            processFiles(task._id, selected);
                                          }}
                                        />
                                      </div>

                                      <div className="files-list">
                                        {(task.attachments || []).length === 0 && (
                                          <p className="no-files-msg">No attachments yet</p>
                                        )}
                                        {(task.attachments || []).map((f, i) => {
                                          const url = f.url || f.path || f.fileUrl || f.secure_url || f.location;

                                          // Server may return originalName (camelCase) — check every variant,
                                          // then fall back to the last part of the URL
                                          let urlName = "";
                                          try {
                                            urlName = decodeURIComponent((url || "").split("?")[0].split("/").pop() || "");
                                          } catch {
                                            urlName = "";
                                          }
                                          const name =
                                            f.originalName || f.originalname ||
                                            f.name || f.filename ||
                                            urlName || "File";

                                          const mime    = f.mimetype || f.mimeType || f.type || "";
                                          const isImage = mime.startsWith("image/") || mime === "image" ||
                                                          /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(name);
                                          const isPdf   = mime === "application/pdf" || /\.pdf$/i.test(name);

                                          return (
                                            <div key={f._id || i} className="file-item">

                                              {/* IMAGE */}
                                              {isImage && url ? (
                                                <img
                                                  className="file-thumb"
                                                  src={url}
                                                  alt={name}
                                                  onClick={() => setPreviewImage(url)}
                                                  style={{ cursor: "pointer" }}
                                                />
                                              ) : isPdf ? (
                                                /* PDF */
                                                <div
                                                  className="pdf-thumb"
                                                  onClick={() => url && setPreviewPdf(url)}
                                                  style={{ cursor: "pointer" }}
                                                >
                                                  <span className="pdf-icon">📕</span>
                                                  <span className="pdf-label">PDF</span>
                                                </div>
                                              ) : (
                                                /* OTHER */
                                                <div className="file-icon-box">{getFileIcon(mime)}</div>
                                              )}

                                              <span className="file-name">{name}</span>

                                              {/* DOWNLOAD */}
                                              {url && (
                                                <button
                                                  className="file-download-btn"
                                                  title="Download"
                                                  onClick={(e) => { e.stopPropagation(); downloadFile(url, name); }}
                                                >
                                                  ⬇
                                                </button>
                                              )}

                                              {/* DELETE */}
                                              <button
                                                className="file-remove"
                                                title="Delete"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteAttachment(task._id, f);
                                                }}
                                              >
                                                ✕
                                              </button>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>

                                    {/* ── COMMENTS ── */}
                                    <div className="comments-section">
                                      <h4>Comments</h4>
                                      <div className="comments-list">
                                        {(task.comments || []).map((c, i) => {
                                          const author = c.author?.userName || c.author?.name || c.author || "User";
                                          const text   = c.text || c.comment || "";
                                          const time   = c.createdAt || c.time || Date.now();
                                          const isMe   = author === getUserName(currentUser);
                                          const commentId = c._id || c.id || c.commentId;
                                          // Only show delete button if comment has a valid server ID (not a temporary ID)
                                          const hasValidId = commentId && typeof commentId === 'string' && commentId.length > 10;
                                          return (
                                            <div key={i} className={`comment-bubble ${isMe ? "me" : "them"}`}>
                                              <div className="bubble-header">
                                                {!isMe && <span className="bubble-author">{author}</span>}
                                                {hasValidId && (
                                                  <button
                                                    className="delete-comment-btn"
                                                    onClick={() => deleteComment(task._id, commentId, author)}
                                                    title="Delete comment"
                                                  >
                                                    🗑️
                                                  </button>
                                                )}
                                              </div>
                                              <p className="bubble-text">{text}</p>
                                              <span className="bubble-time">{formatTime(time)}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                      <input
                                        className="comment-input"
                                        placeholder="Write a comment & press Enter..."
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            addComment(task._id, e.target.value);
                                            e.target.value = "";
                                          }
                                        }}
                                      />
                                    </div>

                                    {/* ── ACTIVITY ── */}
                                    <div className="activity-section">
                                      <h4>Activity</h4>
                                      {(activityLog[task._id] || []).slice().reverse().map((log, i) => (
                                        <div key={i} className="log-item">
                                          <span className="log-text">{log.text}</span>
                                          <span className="log-time">{formatTime(log.time)}</span>
                                        </div>
                                      ))}
                                      {!activityLog[task._id]?.length && (
                                        <p className="no-activity">No activity yet</p>
                                      )}
                                    </div>
                                  </div>
                                )}

                                <div className="card-footer">
                                  {canManageTaskAsCreator(task, currentUserId, isAdmin) && (
                                    <>
                                      <button className="edit-btn"   onClick={() => openEditModal(task)}>Edit</button>
                                      <button className="delete-btn" onClick={() => deleteTask(task._id)}>Delete</button>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              );
            })}
          </div>
        </DragDropContext>

        {/* PAGINATION CONTROLS */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onChange={setCurrentPage}
        />
        </>
      )}

      {/* IMAGE PREVIEW */}
      {previewImage && (
        <div className="media-overlay" onClick={() => setPreviewImage(null)}>
          <img src={previewImage} className="media-full" alt="preview" />
          <button className="media-close" onClick={() => setPreviewImage(null)}>✕</button>
        </div>
      )}

      {/* PDF PREVIEW */}
      {previewPdf && (
        <div className="media-overlay" onClick={() => setPreviewPdf(null)}>
          <div className="pdf-viewer" onClick={(e) => e.stopPropagation()}>
            <div className="pdf-viewer-header">
              <span>PDF Preview</span>
              <button onClick={() => setPreviewPdf(null)}>✕ Close</button>
            </div>
            <iframe src={previewPdf} title="PDF" className="pdf-iframe" />
          </div>
        </div>
      )}

      {/* DELAY MODAL */}
      {delayModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Request Delay</h2>
            <textarea
              className="task-textarea"
              placeholder="Reason for delay..."
              value={delayReason}
              onChange={(e) => setDelayReason(e.target.value)}
            />
            <div className="modal-actions">
              <button className="save"   onClick={submitDelay}>Submit</button>
              <button className="cancel" onClick={() => { setDelayModal(null); setDelayReason(""); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* TASK DETAIL MODAL */}
      {taskDetailModal && (
        <div className="modal-overlay" onClick={() => setTaskDetailModal(null)}>
          <div className="modal modal-task modal-task-detail" onClick={(e) => e.stopPropagation()}>
            <h2>Task Details</h2>
            <div className="modal-task-body">
              <div className="task-detail-section">
                <label>Title</label>
                <p className="task-detail-text">{taskDetailModal.title}</p>
              </div>

              <div className="task-detail-section">
                <label>Description</label>
                <p className="task-detail-text">{taskDetailModal.description || "No description"}</p>
              </div>

              <div className="task-detail-section">
                <label>Assignee</label>
                <p className="task-detail-text">
                  {taskDetailModal.assignedTo?.length
                    ? taskDetailModal.assignedTo.map((a) => a?.userName || a?.name).filter(Boolean).join(", ") || "Unknown"
                    : taskDetailModal.assigneeUserName || "Unknown"}
                </p>
              </div>

              <div className="task-detail-section">
                <label>Department</label>
                <p className="task-detail-text">{taskDetailModal.assigneeDepartment || "—"}</p>
              </div>

              <div className="task-detail-section">
                <label>Position</label>
                <p className="task-detail-text">{taskDetailModal.position || "—"}</p>
              </div>

              <div className="task-detail-section">
                <label>Priority</label>
                <p className="task-detail-text">{taskDetailModal.priority}</p>
              </div>

              <div className="task-detail-section">
                <label>Status</label>
                <p className="task-detail-text">{columnTitles[taskDetailModal.status] || taskDetailModal.status}</p>
              </div>

              <div className="task-detail-section">
                <label>Deadline</label>
                <p className="task-detail-text">{taskDetailModal.deadline ? formatDeadline(taskDetailModal.deadline) : "Not set"}</p>
              </div>

              <div className="task-detail-section">
                <label>Created At</label>
                <p className="task-detail-text">{taskDetailModal.createdAt ? formatTime(taskDetailModal.createdAt) : "—"}</p>
              </div>

              <div className="task-detail-section">
                <label>Last Updated</label>
                <p className="task-detail-text">{taskDetailModal.updatedAt ? formatTime(taskDetailModal.updatedAt) : "—"}</p>
              </div>
            </div>

            <div className="modal-actions">
              <button className="cancel" onClick={() => setTaskDetailModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE / EDIT MODAL */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal modal-task">
            <h2>{editingTask ? "Edit Task" : "Create Task"}</h2>
            <div className="modal-task-body">

              <input
                ref={titleRef}
                placeholder="Title *"
                value={taskData.title}
                onChange={(e) => setTaskData({ ...taskData, title: e.target.value })}
              />

              <textarea
                className="task-textarea"
                placeholder="Description (optional)"
                value={taskData.description}
                onChange={(e) => setTaskData({ ...taskData, description: e.target.value })}
              />

              <label className="input-label">Department *</label>
              <select
                ref={deptRef}
                value={taskData.departmentPick || ""}
                onChange={(e) => setTaskData({ ...taskData, departmentPick: e.target.value, assignedTo: [] })}
                disabled={Boolean(editingTask)}
              >
                <option value="">Select department</option>
                {Object.keys(POSITION_OPTIONS).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>

              <div className="task-field">
                <label>Assign To *</label>
                <div ref={assigneeRef} className="assignee-checkboxes">
                  {taskData.departmentPick && (users || [])
                    .filter(u => getUserDepartment(u) === taskData.departmentPick)
                    .map((u) => (
                      <label key={getUserId(u)} className="assignee-checkbox">
                        <input
                          type="checkbox"
                          checked={taskData.assignedTo.includes(getUserId(u))}
                          onChange={(e) => {
                            const userId = getUserId(u);
                            if (e.target.checked) {
                              setTaskData({
                                ...taskData,
                                assignedTo: [...taskData.assignedTo, userId],
                              });
                            } else {
                              setTaskData({
                                ...taskData,
                                assignedTo: taskData.assignedTo.filter(id => id !== userId),
                              });
                            }
                          }}
                        />
                        <span>{getUserName(u)}</span>
                      </label>
                    ))}
                  {!taskData.departmentPick && <p className="no-dept-msg">Select a department first</p>}
                </div>
              </div>

              <div className="priority-selector" ref={priorityRef}>
                <label>Priority</label>
                <div className="priority-options">
                  {PRIORITIES.map((p) => (
                    <button
                      key={p} type="button"
                      className={`priority-opt${taskData.priority === p ? " selected" : ""}`}
                      style={{
                        borderColor: priorityColors[p],
                        background:  taskData.priority === p ? priorityColors[p] : "transparent",
                        color:       taskData.priority === p ? "#fff" : priorityColors[p],
                      }}
                      onClick={() => setTaskData({ ...taskData, priority: p })}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div className="deadline-toggle-row">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={taskData.hasDeadline}
                    onChange={(e) => setTaskData({
                      ...taskData,
                      hasDeadline: e.target.checked,
                      deadline: e.target.checked ? taskData.deadline : "",
                    })}
                  />
                  <span>Set a deadline</span>
                </label>
              </div>
              {taskData.hasDeadline && (
                <input
                  type="datetime-local"
                  value={taskData.deadline}
                  onChange={(e) => setTaskData({ ...taskData, deadline: e.target.value })}
                  style={{ colorScheme: "dark" }}
                />
              )}

            </div>

            <div className="modal-actions">
              <button className="save" onClick={handleSaveTask}>
                {editingTask ? "Save Changes" : "Create"}
              </button>
              <button className="cancel" onClick={() => { setShowModal(false); setEditingTask(null); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <FieldToast
        message={fieldToast.message}
        targetRef={fieldToast.targetRef}
        show={fieldToast.show}
      />
    </div>
  );
}